import React from "react";
import { MenuItem, Select, FormHelperText, makeStyles, OutlinedInput, FormControl, InputLabel } from "@material-ui/core";
import { Query } from "react-apollo";
import Loading from "../Loading/Loading";
import classNames from "classnames";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
  select: {
    marginTop: theme.spacing(1),
    height: 48,
    width: "100%",
  },
}));

interface Props {
  label: string;
  errorText?: string | undefined;
  touched?: boolean;
  handleChange:
    | ((event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>, child: React.ReactNode) => void)
    | undefined;
  value: string | number | boolean | object | (string | number | boolean | object)[] | undefined;
  handleBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  name: string;
  query: any;
  variables?: any;
  nameQuery: string;
  required?: boolean;
  disabled?: boolean;
  queryOnFocus?: boolean;
  selectAll?: boolean;
}

export const SelectCombo = React.memo(
  ({
    errorText,
    touched,
    handleChange,
    value,
    handleBlur,
    name,
    query,
    variables,
    nameQuery,
    required,
    disabled,
    label,
    queryOnFocus = false,
    selectAll = false
  }: Props) => {
    const classes = useStyles();
    const inputLabel = React.useRef(null);
    const [labelWidth, setLabelWidth] = React.useState(0);
    // carregamento tardio: quando queryOnFocus, so monta o <Query> apos o foco.
    // Montar um <Query skip> gera fetchPolicy "standby", que a apollo-client
    // rejeita no watchQuery e derruba a tela. Por isso o placeholder abaixo nao
    // monta <Query> enquanto o campo nao recebe foco.
    const [carregar, setCarregar] = React.useState(!queryOnFocus);
    React.useEffect(() => {
      if (inputLabel != null && inputLabel.current != null) {
        setLabelWidth((inputLabel.current as any).offsetWidth as number);
      }
    }, []);

    const ativarCarregamento = React.useCallback(() => {
      if (queryOnFocus) {
        setCarregar(true);
      }
    }, [queryOnFocus]);

    if (!carregar) {
      return (
        <FormControl
          variant="outlined"
          className={classNames(classes.formControl)}
          id={`${name}-form-control`}
          onFocus={ativarCarregamento}
        >
          <InputLabel ref={inputLabel} id={`${name}-label`}>
            {label}
          </InputLabel>
          <Select
            fullWidth
            disabled={disabled}
            value={value != null ? value : ""}
            onChange={handleChange}
            onBlur={handleBlur}
            name={name}
            labelId={`${name}-label`}
            onFocus={ativarCarregamento}
            className={classNames(classes.select)}
            input={<OutlinedInput labelWidth={labelWidth} name={name} id={`outlined-${name}`} />}
          >
            <MenuItem value="">
              <em>Selecione</em>
            </MenuItem>
          </Select>
          {errorText && touched && <FormHelperText id={`${name}-error`}>{errorText}</FormHelperText>}
        </FormControl>
      );
    }

    return (
      <Query<any, any> query={query} variables={variables} errorPolicy="all">
        {({ data, loading }) => {
          if (loading) {
            return <Loading />;
          }
          if (!loading && data) {
            const list: Array<any> = data[nameQuery];
            if (list) {
              const items = list
                .filter((obj) => obj.valor !== "" && obj.valor != null)
                .map((obj) => (
                  <MenuItem key={obj.valor} value={obj.valor}>
                    {obj.label}
                  </MenuItem>
                ));
              
                if(selectAll) {
                  items.unshift(
                    <MenuItem value={-999} key="selectAll">
                      <em>Todos</em>
                    </MenuItem>
                  );
                }
              if (!required) {
                items.unshift(
                  <MenuItem value="" key="">
                    <em>Selecione</em>
                  </MenuItem>
                );
              }
              return (
                <React.Fragment>
                  <FormControl
                    variant="outlined"
                    className={classNames(classes.formControl)}
                    error={!!errorText && !!touched}
                    id={`${name}-form-control`}
                  >
                    <InputLabel ref={inputLabel} id={`${name}-label`}>
                      {label}
                    </InputLabel>
                    <Select
                      fullWidth
                      disabled={disabled}
                      value={value != null ? value : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name={name}
                      labelId={`${name}-label`}
                      className={classNames(classes.select)}
                      input={<OutlinedInput labelWidth={labelWidth} name={name} id={`outlined-${name}`} />}
                    >
                      {items}
                    </Select>
                    {errorText && touched && <FormHelperText id={`${name}-error`}>{errorText}</FormHelperText>}
                  </FormControl>
                </React.Fragment>
              );
            }
          }
          return (
            <React.Fragment>
              <FormControl variant="outlined" className={classNames(classes.formControl)} id={`${name}-form-control`}>
                <InputLabel ref={inputLabel} id={`${name}-label`}>
                  {label}
                </InputLabel>
                <Select
                  fullWidth
                  disabled={disabled}
                  value=""
                  onChange={handleChange}
                  name={name}
                  labelId={`${name}-label`}
                  className={classNames(classes.select)}
                  input={<OutlinedInput labelWidth={labelWidth} name={name} id={`outlined-${name}`} />}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                </Select>
                {(errorText || touched) && <FormHelperText id={`${name}-error`}>{errorText}</FormHelperText>}
              </FormControl>
            </React.Fragment>
          );
        }}
      </Query>
    );
  }
);
